<script setup lang="ts">
import { SolAlert, SolButton, SolInputText, SolInputTextPassword } from '@solfacil/girassol'
import IconInfoOutline from '~icons/material-symbols/info-outline'
import OpenInNew from '~icons/material-symbols/open-in-new'
import RevalidationAuthBox from './RevalidationAuthBox.vue'

const props = defineProps<{
  submitContextAttr: string
  portalName: string
  portalExternalHref: string | null
  bannerInverterBrandLabel: string
  registerCredentialErrorMessage?: string | null
  submitRevalidationsErrorMessage?: string | null
}>()

const emit = defineEmits<{
  openHelp: []
}>()

watch(() => [props.registerCredentialErrorMessage, props.submitRevalidationsErrorMessage], ([credErr, submitErr]) => {
  if (credErr || submitErr) {
    const id = credErr ? 'revalidation-register-credential-error' : 'revalidation-submit-revalidations-error-step2'
    nextTick(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    })
  }
})

const installerLogin = defineModel<string>('installerLogin', { required: true })
const installerPassword = defineModel<string>('installerPassword', { required: true })
const portalConnectionAuthorized = defineModel<boolean>('portalConnectionAuthorized', { required: true })

const PRIVACY_POLICY_URL = 'https://landing.solfacil.com.br/politica-de-privacidade/'

function onPortalConnectionAuthChange() {
  portalConnectionAuthorized.value = !portalConnectionAuthorized.value
}
</script>

<template>
  <div
    class="revalidation-submit-modal-step2"
    :data-submit-context="submitContextAttr"
  >
    <SolAlert
      v-if="registerCredentialErrorMessage"
      id="revalidation-register-credential-error"
      title="Não foi possível conectar"
      :text="registerCredentialErrorMessage"
      feedback="error"
    />
    <SolAlert
      v-if="submitRevalidationsErrorMessage"
      id="revalidation-submit-revalidations-error-step2"
      title="Não foi possível enviar"
      :text="submitRevalidationsErrorMessage"
      feedback="error"
    />

    <p class="revalidation-submit-modal-connect-intro">
      Por favor, informe as suas <strong>credenciais de instalador</strong> do portal de monitoramento
      <a
        v-if="portalExternalHref"
        :href="portalExternalHref"
        class="revalidation-submit-modal-connect-intro-link"
        target="_blank"
        rel="noopener noreferrer"
        :aria-label="`Abrir o portal ${portalName} em nova aba`"
      >{{ portalName }}.</a>
      <strong v-else>{{ portalName }}.</strong>
    </p>

    <div
      class="revalidation-submit-modal-portal-info"
      role="region"
      aria-label="Informações sobre o portal de monitoramento"
    >
      <div class="revalidation-submit-modal-portal-info-visual">
        <img
          class="revalidation-submit-modal-portal-info-img"
          src="/images/onboarding-home-modal.svg"
          alt=""
          loading="lazy"
          decoding="async"
        >
      </div>
      <div class="revalidation-submit-modal-portal-info-copy">
        <p class="revalidation-submit-modal-portal-info-lead">
          Instalações com inversores {{ bannerInverterBrandLabel }} só podem ser validadas por meio do portal {{ portalName }}.
        </p>
        <p class="revalidation-submit-modal-portal-info-follow">
          Caso ainda não tenha conta,
          <a
            v-if="portalExternalHref"
            :href="portalExternalHref"
            class="revalidation-submit-modal-inline-link"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="`Cadastrar-se no portal ${portalName} (abre em nova aba)`"
          >cadastre-se</a>
          <strong v-else>cadastre-se</strong>
          e migre já as suas plantas.
        </p>
      </div>
      <SolButton
        id="revalidation-submit-connect-questions"
        variant="secondary"
        size="small"
        class="revalidation-submit-modal-portal-info-help"
        @click="emit('openHelp')"
      >
        Dúvidas?
      </SolButton>
    </div>

    <div
      class="revalidation-submit-modal-credentials-form"
      role="group"
      aria-label="Credenciais do portal de monitoramento"
    >
      <span class="revalidation-submit-modal-credentials-form-label revalidation-submit-modal-credentials-form-label--portal">
        Portal
      </span>
      <div class="revalidation-submit-modal-credentials-form-value revalidation-submit-modal-credentials-form-value--portal">
        <a
          v-if="portalExternalHref"
          :href="portalExternalHref"
          class="revalidation-submit-modal-portal-link"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="`Abrir o portal ${portalName} em nova aba`"
        >
          <span>{{ portalName }}</span>
          <OpenInNew class="revalidation-submit-modal-portal-link-icon" aria-hidden="true" />
        </a>
        <span v-else class="revalidation-submit-modal-portal-static">{{ portalName }}</span>
      </div>

      <label
        class="revalidation-submit-modal-credentials-form-label revalidation-submit-modal-credentials-form-label--user"
        for="revalidation-submit-installer-login"
      >
        Usuário de instalador
      </label>
      <div class="revalidation-submit-modal-credentials-form-value revalidation-submit-modal-credentials-form-value--user">
        <SolInputText
          id="revalidation-submit-installer-login"
          v-model="installerLogin"
          name="revalidation-installer-login"
          placeholder="Digite o usuário"
          size="medium"
          class="revalidation-submit-modal-credential-input revalidation-submit-modal-credential-input--bare"
        />
      </div>
      <p class="revalidation-submit-modal-credentials-form-helper">
        A validação só pode ser feita com o seu usuário de instalador.
      </p>

      <label
        class="revalidation-submit-modal-credentials-form-label revalidation-submit-modal-credentials-form-label--pass"
        for="revalidation-submit-installer-password"
      >
        Senha*
      </label>
      <div class="revalidation-submit-modal-credentials-form-value revalidation-submit-modal-credentials-form-value--pass">
        <SolInputTextPassword
          id="revalidation-submit-installer-password"
          v-model="installerPassword"
          name="revalidation-installer-password"
          placeholder="Digite a senha"
          required
          size="medium"
          class="revalidation-submit-modal-credential-input revalidation-submit-modal-credential-input--bare"
        />
      </div>
    </div>

    <div
      class="revalidation-submit-modal-informative-hint"
      role="note"
    >
      <IconInfoOutline class="revalidation-submit-modal-informative-hint-icon" aria-hidden="true" />
      <div class="revalidation-submit-modal-informative-hint-body">
        <strong>Evite reprovações:</strong>
        certifique-se de ter criado a planta de monitoramento com o usuário informado acima no portal
        <a
          v-if="portalExternalHref"
          :href="portalExternalHref"
          class="revalidation-submit-modal-inline-link"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="`Abrir o portal ${portalName} em nova aba`"
        >{{ portalName }}</a>
        <strong v-else>{{ portalName }}</strong>.
      </div>
    </div>

    <RevalidationAuthBox
      id="revalidation-submit-portal-auth"
      name="revalidation-submit-portal-auth"
      value="portal-auth"
      :checked="portalConnectionAuthorized"
      @change="onPortalConnectionAuthChange"
    >
      <span>
        Autorizo a Solfácil a utilizar o meu acesso de instalador do Portal {{ portalName }} para acompanhar os projetos e sua operação, conforme a
        <a
          class="revalidation-submit-modal-inline-link"
          :href="PRIVACY_POLICY_URL"
          target="_blank"
          rel="noopener noreferrer"
        >Política de Privacidade</a>.
      </span>
    </RevalidationAuthBox>
  </div>
</template>

<style scoped lang="scss">
.revalidation-submit-modal-step2 {
  @apply flex flex-col gap-6 w-full min-w-0;

  .revalidation-submit-modal-connect-intro {
    margin: 0;
    @apply fonts-body-large-regular text-neutral-low-pure leading-[1.625];

    strong {
      @apply fonts-body-large-bold text-neutral-low-pure;
    }

    .revalidation-submit-modal-connect-intro-link {
      @apply fonts-body-large-bold text-brand-primary-pure underline;
      text-underline-offset: 2px;

      &:focus-visible {
        outline: 2px solid theme('colors.brand.primary.pure');
        outline-offset: 2px;
      }
    }
  }

  .revalidation-submit-modal-portal-info {
    @apply flex flex-row flex-wrap items-center gap-4;
    @apply rounded-md border border-neutral-high-medium bg-neutral-high-light;
    @apply px-4 py-4;

    .revalidation-submit-modal-portal-info-visual {
      flex-shrink: 0;
    }

    .revalidation-submit-modal-portal-info-img {
      display: block;
      width: 7rem;
      height: auto;
      max-width: 100%;
      @apply object-contain object-left;

      @screen sm {
        width: 8.25rem;
      }

      @screen md:system {
        width: 9rem;
      }
    }

    .revalidation-submit-modal-portal-info-copy {
      @apply flex flex-col gap-1;
      flex: 1 1 14rem;
      min-width: 0;
    }

    .revalidation-submit-modal-portal-info-lead {
      margin: 0;
      @apply fonts-body-medium-bold text-neutral-low-pure leading-[1.5];
    }

    .revalidation-submit-modal-portal-info-follow {
      margin: 0;
      @apply fonts-body-medium-regular text-neutral-low-pure leading-[1.5];
    }

    .revalidation-submit-modal-portal-info-help {
      flex-shrink: 0;
      margin-left: auto;

      @screen max-md:system {
        margin-left: 0;
        width: 100%;
      }
    }
  }

  .revalidation-submit-modal-credentials-form {
    display: grid;
    width: 100%;
    align-items: start;
    column-gap: 1.25rem;
    row-gap: 0.375rem;

    @screen max-md:system {
      grid-template-columns: minmax(0, 1fr);
    }

    @screen md:system {
      grid-template-columns: max-content minmax(0, 1fr) minmax(0, 1fr);
      grid-template-rows: auto auto auto;

      .revalidation-submit-modal-credentials-form-label--portal {
        grid-column: 1;
        grid-row: 1;
        justify-self: start;
        width: max-content;
        max-width: 100%;
        padding-right: 1rem;
      }

      .revalidation-submit-modal-credentials-form-value--portal {
        grid-column: 1;
        grid-row: 2;
        justify-self: start;
        width: max-content;
        max-width: 100%;
        padding-right: 1rem;
      }

      .revalidation-submit-modal-credentials-form-label--user {
        grid-column: 2;
        grid-row: 1;
      }

      .revalidation-submit-modal-credentials-form-value--user {
        grid-column: 2;
        grid-row: 2;
      }

      .revalidation-submit-modal-credentials-form-helper {
        grid-column: 2;
        grid-row: 3;
      }

      .revalidation-submit-modal-credentials-form-label--pass {
        grid-column: 3;
        grid-row: 1;
      }

      .revalidation-submit-modal-credentials-form-value--pass {
        grid-column: 3;
        grid-row: 2;
      }
    }
  }

  .revalidation-submit-modal-credentials-form-label {
    margin: 0;
    display: block;
    @apply fonts-body-medium-bold text-neutral-low-pure leading-[1.375];
  }

  .revalidation-submit-modal-credentials-form-value {
    min-width: 0;
    width: 100%;

    &--portal {
      width: auto;
      max-width: 100%;
    }

    .revalidation-submit-modal-credential-input--bare {
      @apply w-full max-w-full;
    }
  }

  .revalidation-submit-modal-credentials-form-helper {
    margin: 0;
    @apply fonts-body-small-regular text-neutral-low-light leading-[1.5];
  }

  .revalidation-submit-modal-informative-hint {
    @apply flex flex-row gap-3 items-start;
    @apply rounded-md border border-feedback-informative-medium bg-feedback-informative-light;
    @apply px-4 py-3;

    .revalidation-submit-modal-informative-hint-icon {
      flex-shrink: 0;
      width: 1.25rem;
      height: 1.25rem;
      margin-top: 2px;
      @apply text-feedback-informative-pure;
    }

    .revalidation-submit-modal-informative-hint-body {
      min-width: 0;
      @apply fonts-body-medium-regular text-feedback-informative-dark leading-[1.5];

      strong {
        @apply fonts-body-medium-bold;
      }

      .revalidation-submit-modal-inline-link {
        @apply fonts-body-medium-bold text-feedback-informative-pure underline;
        text-underline-offset: 2px;

        &:focus-visible {
          outline: 2px solid theme('colors.feedback.informative.pure');
          outline-offset: 2px;
        }
      }
    }
  }

  .revalidation-submit-modal-portal-link {
    @apply inline-flex flex-row items-center gap-1 self-start;
    @apply fonts-body-medium-regular text-brand-primary-medium underline cursor-pointer;
    text-underline-offset: 2px;

    &:focus-visible {
      outline: 2px solid theme('colors.brand.primary.pure');
      outline-offset: 2px;
    }

    .revalidation-submit-modal-portal-link-icon {
      width: 1.125rem;
      height: 1.125rem;
      flex-shrink: 0;
      @apply text-brand-primary-medium;
    }
  }

  .revalidation-submit-modal-portal-static {
    @apply fonts-body-medium-regular text-neutral-low-medium;
  }

  .revalidation-submit-modal-inline-link {
    @apply fonts-body-medium-bold text-brand-primary-pure underline;
    text-underline-offset: 2px;

    &:focus-visible {
      outline: 2px solid theme('colors.brand.primary.pure');
      outline-offset: 2px;
    }
  }
}
</style>
